import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { BaseHttpService } from './base-http.service';
import { ConfigService } from './config.service';
import { AuthenticationService } from './authentication.service';
import { LocalStorageService } from './local-storage.service';

/** Why the assistant is not available (node-restify biz/AiScope.js ACCESS_DENIED) */
export type AiAccessReason = 'disabled' | 'notAppUser' | 'aiNotEnabled' | 'noPermission' | null;

export interface AiAccess {
  available: boolean;
  reason: AiAccessReason;
}

/** One server-sent event of POST ai/ask */
export interface AiAskEvent {
  event: 'agent_started' | 'tool_call' | 'topic_changed' | 'final' | 'error' | 'heartbeat' | string;
  data: any;
}

export interface AiTopicSummary {
  conversationId: string;
  title: string;
  messageCount: number;
  updated: string;
  sharedWith?: number;
  owner?: string;
  readOnly?: boolean;
}

export interface AiTopicMessage {
  role: 'user' | 'assistant';
  text: string;
  sources?: Array<{ agent: string }>;
  created?: string;
}

export interface AiTopic {
  conversationId: string;
  title: string;
  readOnly: boolean;
  updated: string;
  messages: Array<AiTopicMessage>;
}

/**
 * AI assistant ("Ask your data", Sentiment Analysis - V3) API.
 */
@Injectable()
export class AiAssistantService extends BaseHttpService {

  constructor(
    public http: HttpClient,
    private configService: ConfigService,
    private authenticationService: AuthenticationService,
    private localStorageService: LocalStorageService
  ) {
    super(http);
  }

  getAccess(): Observable<any> {
    return this.get('ai/access');
  }

  listTopics(): Observable<any> {
    return this.get('ai/conversations');
  }

  getTopic(conversationId: string): Observable<any> {
    return this.get('ai/conversations', conversationId);
  }

  renameTopic(conversationId: string, title: string): Observable<any> {
    return this.put('ai/conversations', { title }, conversationId);
  }

  deleteTopic(conversationId: string): Observable<any> {
    return this.delete('ai/conversations', conversationId);
  }

  shareTopic(conversationId: string, userIds: Array<number>): Observable<any> {
    return this.post(`ai/conversations/${conversationId}/share`, { userIds });
  }

  unshareTopic(conversationId: string, userId: number): Observable<any> {
    return this.delete(`ai/conversations/${conversationId}/share`, String(userId));
  }

  copyTopic(conversationId: string): Observable<any> {
    return this.post(`ai/conversations/${conversationId}/copy`, {});
  }

  shareCandidates(search: string): Observable<any> {
    return this.get('ai/share-candidates', undefined, { search });
  }

  recordFeedback(question: string, answer: string, reaction: 'ThumbsUp' | 'ThumbsDown', userComment?: string): Observable<any> {
    const body: any = { question, answer, reaction };
    if (userComment) {
      body.userComment = userComment;
    }
    return this.post('rag-feedback', body);
  }

  /**
   * Asks a question and streams the answer (server-sent events). HttpClient cannot read a response stream, so this
   * uses fetch with the same headers the HTTP interceptor adds.
   * @param question - the user's question
   * @param conversationId - the topic to continue, or null for a new topic
   * @param onEvent - called for every event (agent_started, tool_call, topic_changed, final, error)
   * @param signal - aborts the request (e.g. when the user leaves the page)
   */
  async ask(question: string, conversationId: string | null, onEvent: (e: AiAskEvent) => void, signal?: AbortSignal): Promise<void> {
    const headers: any = { 'Content-Type': 'application/json' };
    const token = this.localStorageService.get(this.configService.localStorageKeys.AuthToken);
    if (token) {
      headers['x-survale-auth-token'] = token;
    }
    const clientId = this.authenticationService.getCurrentUser()?.clientId;
    if (clientId) {
      headers['x-survale-client-id'] = String(clientId);
    }
    let response: Response;
    try {
      response = await fetch(`${this.restHost}/v0/ai/ask`, {
        method: 'POST', headers, body: JSON.stringify({ question, conversationId }), signal
      });
    } catch (error) {
      if (!signal?.aborted) {
        onEvent({ event: 'error', data: { message: 'Could not reach the assistant. Please check your connection and try again.' } });
      }
      return;
    }
    if (!response.ok || !response.body) {
      let body: any = {};
      try {
        body = await response.json();
      } catch {
        // not JSON
      }
      onEvent({ event: 'error', data: { message: body.error || 'The assistant is not available right now.', reason: body.reason, status: response.status } });
      return;
    }
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    try {
      while (true) {
        const { value, done } = await reader.read();
        if (done) {
          break;
        }
        buffer += decoder.decode(value, { stream: true });
        let sep = buffer.indexOf('\n\n');
        while (sep > -1) {
          const block = buffer.slice(0, sep);
          buffer = buffer.slice(sep + 2);
          const parsed = this.parseEvent(block);
          if (parsed) {
            onEvent(parsed);
          }
          sep = buffer.indexOf('\n\n');
        }
      }
    } catch (error) {
      if (!signal?.aborted) {
        onEvent({ event: 'error', data: { message: 'The connection to the assistant was interrupted. Please try again.' } });
      }
    }
  }

  private parseEvent(block: string): AiAskEvent | null {
    let event = 'message';
    const data: Array<string> = [];
    for (const line of block.split('\n')) {
      if (line.startsWith('event:')) {
        event = line.slice(6).trim();
      } else if (line.startsWith('data:')) {
        data.push(line.slice(5).trim());
      }
    }
    if (data.length === 0) {
      return null;
    }
    try {
      return { event, data: JSON.parse(data.join('\n')) };
    } catch {
      return null;
    }
  }
}
